import React from "react";
import { Form, Row, Col } from "react-bootstrap";
import { useIntl } from "react-intl";
import styled from "styled-components";
import { AmountDelta, DeltaTypeEnum, MonetaryAmount } from "../domain/upgradeConfiguration.schema";
import { upgradeManagerMessages } from "../upgradeManager.messages";
import { PriceInput } from "../../shared/widgets/PriceInput";

// Extend PriceInput to fill the column width (PriceInput defaults to 120px)
const FullWidthPriceInput = styled(PriceInput)`
  width: 100%;
`;

interface DeltaFieldsProps {
  label: React.ReactNode;
  delta?: AmountDelta;
  onChange: (delta?: AmountDelta) => void;
  defaultCurrency: string;
  labelCol?: number;
  selectCol?: number;
  valueCol?: number;
  percentageCol?: number;
  currencyCol?: number;
  spacing?: string;
}

export function DeltaFields({
  label,
  delta,
  onChange,
  defaultCurrency,
  labelCol = 2,
  selectCol,
  valueCol = 3,
  percentageCol,
  currencyCol = 1,
  spacing = "mb-2",
}: DeltaFieldsProps): JSX.Element {
  const intl = useIntl();
  const messages = upgradeManagerMessages;
  const resolvedSelectCol = selectCol ?? valueCol;
  const resolvedPercentageCol = percentageCol ?? valueCol;

  const handleTypeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const type = e.target.value as DeltaTypeEnum | "";
    if (!type) {
      onChange(undefined);
    } else {
      onChange({
        deltaType: type,
        value: type === DeltaTypeEnum.PERCENTAGE ? 0 : { amount: 0, currency: defaultCurrency },
      });
    }
  };

  return (
    <>
      <Form.Group as={Row} className={spacing}>
        <Form.Label column sm={labelCol}>
          {label}
        </Form.Label>
        <Col sm={resolvedSelectCol}>
          <Form.Control as="select" value={delta?.deltaType ?? ""} onChange={handleTypeChange}>
            <option value="">{intl.formatMessage(messages.selectOption)}</option>
            <option value={DeltaTypeEnum.MONETARY_AMOUNT}>
              {intl.formatMessage(messages.deltaTypeMonetary)}
            </option>
            <option value={DeltaTypeEnum.PERCENTAGE}>
              {intl.formatMessage(messages.deltaTypePercentage)}
            </option>
          </Form.Control>
        </Col>
      </Form.Group>

      {delta?.deltaType === DeltaTypeEnum.PERCENTAGE && (
        <Form.Group as={Row} className={spacing}>
          <Form.Label column sm={labelCol} />
          <Col sm={resolvedPercentageCol}>
            <FullWidthPriceInput
              placeholder={intl.formatMessage(messages.percentagePlaceholder)}
              value={typeof delta.value === "number" ? delta.value * 100 : 0}
              onChange={value => onChange({ ...delta, value: value / 100 })}
            />
          </Col>
        </Form.Group>
      )}

      {delta?.deltaType === DeltaTypeEnum.MONETARY_AMOUNT && typeof delta.value === "object" && (
        <Form.Group as={Row} className={spacing}>
          <Form.Label column sm={labelCol} />
          <Col sm={valueCol}>
            <FullWidthPriceInput
              placeholder={intl.formatMessage(messages.amountPlaceholder)}
              value={(delta.value as MonetaryAmount).amount || 0}
              onChange={value =>
                onChange({
                  ...delta,
                  value: {
                    ...(delta.value as MonetaryAmount),
                    amount: value,
                  },
                })
              }
            />
          </Col>
          <Col sm={currencyCol}>
            <Form.Control
              type="text"
              value={(delta.value as MonetaryAmount).currency}
              readOnly
              disabled
            />
          </Col>
        </Form.Group>
      )}
    </>
  );
}
